// Design System Button Component
import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DESIGN_TOKENS, TYPOGRAPHY_TOKENS } from '../tokens';

// Extended button variants for agrotech brand
type ButtonVariant = 'primary' | 'secondary' | 'sage' | 'success' | 'warning' | 'error' | 'ghost' | 'link';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
}

// Styled button with design system tokens
const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !['variant', 'size', 'loading'].includes(prop as string),
})<ButtonProps>(({ theme, variant = 'primary', size = 'medium', loading }) => {
  const sizeStyles = {
    small: {
      padding: '8px 16px',
      fontSize: TYPOGRAPHY_TOKENS.fontSizes.sm,
      minHeight: '36px',
    },
    medium: {
      padding: '12px 24px',
      fontSize: TYPOGRAPHY_TOKENS.fontSizes.base,
      minHeight: '44px',
    },
    large: {
      padding: '16px 32px',
      fontSize: TYPOGRAPHY_TOKENS.fontSizes.lg,
      minHeight: '52px',
    },
  };

  const variantStyles = {
    primary: {
      background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[600]} 0%, ${DESIGN_TOKENS.colors.primary[500]} 100%)`,
      color: DESIGN_TOKENS.colors.text.inverse,
      border: 'none',
      '&:hover': {
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[800]} 0%, ${DESIGN_TOKENS.colors.primary[700]} 100%)`,
        boxShadow: `0 4px 12px rgba(12, 82, 64, 0.15)`,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: DESIGN_TOKENS.colors.primary[600],
      border: `1px solid ${DESIGN_TOKENS.colors.primary[600]}`,
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.primary[600],
        color: DESIGN_TOKENS.colors.text.inverse,
        transform: 'translateY(-1px)',
      },
    },
    sage: {
      backgroundColor: DESIGN_TOKENS.colors.primary[400],
      color: DESIGN_TOKENS.colors.text.inverse,
      border: 'none',
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.primary[500],
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(12, 82, 64, 0.15)',
      },
    },
    success: {
      backgroundColor: DESIGN_TOKENS.colors.status.success[500],
      color: DESIGN_TOKENS.colors.text.inverse,
      border: 'none',
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.status.success[600],
        transform: 'translateY(-1px)',
      },
    },
    warning: {
      backgroundColor: DESIGN_TOKENS.colors.status.warning[500],
      color: DESIGN_TOKENS.colors.text.inverse,
      border: 'none',
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.status.warning[600],
        transform: 'translateY(-1px)',
      },
    },
    error: {
      backgroundColor: DESIGN_TOKENS.colors.status.error[500],
      color: DESIGN_TOKENS.colors.text.inverse,
      border: 'none',
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.status.error[600],
        transform: 'translateY(-1px)',
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: DESIGN_TOKENS.colors.text.secondary,
      border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.neutral[50],
        borderColor: DESIGN_TOKENS.colors.primary[600],
        color: DESIGN_TOKENS.colors.primary[600],
        transform: 'translateY(-1px)',
      },
    },
    link: {
      backgroundColor: 'transparent',
      color: DESIGN_TOKENS.colors.primary[600],
      border: 'none',
      padding: '4px 8px',
      minHeight: 'auto',
      textDecoration: 'underline',
      '&:hover': {
        backgroundColor: 'transparent',
        color: DESIGN_TOKENS.colors.primary[800],
        textDecoration: 'underline',
      },
    },
  };

  return {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: TYPOGRAPHY_TOKENS.fontWeights.semibold,
    fontFamily: TYPOGRAPHY_TOKENS.fonts.primary,
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    ...sizeStyles[size],
    ...(variantStyles[variant as keyof typeof variantStyles] || variantStyles.primary),
    '&:disabled': {
      backgroundColor: DESIGN_TOKENS.colors.neutral[100],
      color: DESIGN_TOKENS.colors.text.disabled,
      cursor: 'not-allowed',
      transform: 'none',
      '&:hover': {
        backgroundColor: DESIGN_TOKENS.colors.neutral[100],
        transform: 'none',
      },
    },
  };
});

// Button component
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant as any}
      size={size as any}
      loading={loading}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? 'Loading...' : children}
    </StyledButton>
  );
};

export default Button;